import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Container,
  Paper,
  ThemeIcon,
  Group,
  Stack,
  Table,
  Badge,
  SimpleGrid,
  Grid,
  Progress,
  Button,
  Divider,
  Box,
  rem,
  Card,
  Loader,
  Center, ScrollArea
} from '@mantine/core';
import {
  IconLayoutDashboard,
  IconSchool,
  IconHome,
  IconCoin,
  IconArrowRight,
  IconClock,
  IconInfoCircle,
  IconUserCheck,
  IconUserX,
  IconAlertCircle,
  IconRefresh
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const heroGradient =
  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

function formatCurrency(value) {
  const num = Number(value || 0);
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' TL';
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Backend'den gelecek özet istatistik verileri için state
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalFamilies: 0,
    totalMonthlyPayment: 0,
    activeCount: 0,
    pendingCount: 0,
    passiveCount: 0
  });

  // Backend'den gelecek son aktiviteler için state
  const [recentActivities, setRecentActivities] = useState([]);

  // Toplam kayıt sayısı (Sıfıra bölünme hatasını engellemek için minimum 1 yapıyoruz)
  const totalRecords = useMemo(() => {
    const total = (stats?.activeCount || 0) + (stats?.pendingCount || 0) + (stats?.passiveCount || 0);
    return total === 0 ? 1 : total;
  }, [stats]);

  // API'den dinamik verileri çeken fonksiyon
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/financial-support/dashboard/summary');
      
      if (res.data) {
        setStats(res.data.stats || stats);
        setRecentActivities(res.data.activities || []);
      }
    } catch (err) {
      notifications.show({
        title: 'Sistem Hatası',
        message: 'Dashboard genel durum verileri sunucudan yüklenemedi.',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        
        {/* KAHRAMAN ÜST PANEL (HERO) */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: heroGradient,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Group gap="sm" align="center">
                <ThemeIcon size={42} radius="xl" variant="white" color="blue">
                  <IconLayoutDashboard size={22} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">Yönetim Paneli</Text>
                  <Title order={1} fw={900} c="white">Genel Durum Özet Paneli</Title>
                </Stack>
              </Group>
              <Button 
                variant="white" 
                color="blue" 
                radius="xl" 
                size="xs"
                leftSection={<IconRefresh size={14} />}
                loading={loading}
                onClick={fetchDashboardData}
                style={{ background: 'rgba(255,255,255,0.14)', color: 'white', border: 'none' }}
              >
                Yenile
              </Button>
            </Group>
            <Text c="rgba(255,255,255,0.78)" size="sm" maw={800}>
              Bursiyer öğrencilerin ve yardım sağlanan ailelerin güncel operasyonel dağılımlarını, bekleyen onay süreçlerini ve aylık finansal bütçe yüklerini tek ekrandan takip edin.
            </Text>
          </Stack>
        </Paper>

        {loading ? (
          /* YÜKLENİYOR EKRANI */
          <Card withBorder radius="xl" p="xl" h={300}>
            <Center h="100%">
              <Stack align="center" gap="xs">
                <Loader size="lg" type="dots" />
                <Text size="sm" c="dimmed">Genel analiz raporları hazırlanıyor...</Text>
              </Stack>
            </Center>
          </Card>
        ) : (
          /* VERİ GÖSTERİM ALANI */
          <>
            {/* METRİK KARTLARI VE YÖNLENDİRME BUTONLARI */}
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Paper radius="xl" p="xl" withBorder shadow="sm" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">Toplam Bursiyer</Text>
                      <Text fw={900} size={rem(32)}>{stats.totalStudents} Öğrenci</Text>
                    </Stack>
                    <ThemeIcon radius="xl" variant="light" color="blue" size={46}>
                      <IconSchool size={22} />
                    </ThemeIcon>
                  </Group>
                  <Text size="sm" c="dimmed" mt="xs">Aktif eğitim ve burs desteği devam eden öğrenciler.</Text>
                </Box>
                <Button 
                  variant="light" 
                  color="blue" 
                  radius="xl" 
                  mt="lg" 
                  rightSection={<IconArrowRight size={16} />}
                  onClick={() => navigate('/financial-support/student')}
                >
                  Öğrencileri Yönet
                </Button>
              </Paper>

              <Paper radius="xl" p="xl" withBorder shadow="sm" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">Yardım Alan Aileler</Text>
                      <Text fw={900} size={rem(32)}>{stats.totalFamilies} Hane</Text>
                    </Stack>
                    <ThemeIcon radius="xl" variant="light" color="green" size={46}>
                      <IconHome size={22} />
                    </ThemeIcon>
                  </Group>
                  <Text size="sm" c="dimmed" mt="xs">Düzenli sosyal ve mali yardım sağlanan haneler.</Text>
                </Box>
                <Button 
                  variant="light" 
                  color="green" 
                  radius="xl" 
                  mt="lg" 
                  rightSection={<IconArrowRight size={16} />}
                  onClick={() => navigate('/financial-support/family')}
                >
                  Aileleri Yönet
                </Button>
              </Paper>

              <Paper radius="xl" p="xl" withBorder shadow="sm" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Aylık Toplam Finansal Miktar</Text>
                    <Text fw={900} size={rem(28)} c="indigo.7">{formatCurrency(stats.totalMonthlyPayment)}</Text>
                  </Stack>
                  <ThemeIcon radius="xl" variant="light" color="indigo" size={46}>
                    <IconCoin size={22} />
                  </ThemeIcon>
                </Group>
                <Text size="sm" c="dimmed" mt="xs" style={{ flexGrow: 1 }}>
                  Burs ve aile yardımı modüllerinin bu ay içinde gerçekleştireceği toplam transfer miktarı.
                </Text>
              </Paper>
            </SimpleGrid>

            {/* ANA DETAY IKI KOLONLU GRID */}
            <Grid columns={12} gutter="lg">
              
              {/* SOL TARAFI: SON İŞLEMLER TABLOSU */}
              <Grid.Col>
                <Paper radius="xl" p={{ base: 'md', md: 'xl' }} withBorder shadow="sm" h="100%">
                  <Group gap="xs" mb="lg">
                    <ThemeIcon variant="subtle" color="gray"><IconClock size={20} /></ThemeIcon>
                    <Text fw={700} size="lg">Son Kayıtlar ve Sistem İşlemleri</Text>
                  </Group>
                  <Divider mb="md" />
                  
                  {recentActivities.length === 0 ? (
                    <Center h={200}>
                      <Text size="sm" c="dimmed" fs="italic">Sistemde henüz kaydedilmiş bir aktivite bulunmuyor.</Text>
                    </Center>
                  ) : (
                    <ScrollArea>
                      <Table verticalSpacing="md" highlightOnHover striped>
                        <Table.Thead bg="var(--mantine-color-gray-0)">
                          <Table.Tr>
                            <Table.Th>İsim / Unvan</Table.Th>
                            <Table.Th>Modül Türü</Table.Th>
                            <Table.Th>İşlem Zamanı</Table.Th>
                            <Table.Th>Bütçe</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {recentActivities.map((activity) => (
                            <Table.Tr key={activity.id}>
                              <Table.Td fw={600}>{activity.name}</Table.Td>
                              <Table.Td>
                                <Badge color={activity.type === 'Öğrenci' ? 'blue' : 'green'} variant="light">
                                  {activity.type}
                                </Badge>
                              </Table.Td>
                              <Table.Td c="dimmed" size="sm">{activity.date}</Table.Td>
                              <Table.Td fw={700}>{formatCurrency(activity.amount)}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  )}
                </Paper>
              </Grid.Col>

            </Grid>
          </>
        )}
      </Stack>
    </Container>
  );
}